import PortalIdParser from 'PortalIdParser';
import memoize from 'transmute/memoize';
import debounce from 'hs-lodash/debounce';
import Raven from 'raven-js';

import {
  CrmObject,
  CrmObjectTypeProperty,
  CrmSearchResponse,
  ObjectId,
} from 'marketing-platform-lib/types';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';

import {
  CrmSearchVariables,
  UpdatedCrmPropertyValue,
} from 'framework-listing-lib/types';
import {
  GraphQLCrmObject,
  GraphQLCrmSearchCacheType,
  GraphQLCrmSearchResponse,
  GraphQLVariables,
} from 'framework-listing-lib/internal/graphql/types';
import { CRM_SEARCH_GRAPHQL_CACHE_KEY } from 'framework-listing-lib/internal/graphql/constants';

const debouncedMissingPropertiesReporter = debounce(
  () => {
    Raven.captureException(
      new Error('CRM search result missing allProperties field')
    );
  },
  // This exception is meant to help us track down other errors that may
  // appear due to the properties being missing
  // If we've fetched 100 rows and they're all missing the allProperties field,
  // we don't want to report 100 errors. We just want to know this issue
  // is occurring generally
  60000
);

export const transformGraphQLCrmObjectToCrmObject = (
  graphQLCrmObject: GraphQLCrmObject,
  objectTypeId: string
): CrmObject => {
  if (!graphQLCrmObject.allProperties) {
    debouncedMissingPropertiesReporter();
  }

  return {
    objectId: graphQLCrmObject.objectId,
    objectTypeId,
    portalId: Number(PortalIdParser.get()),
    properties:
      graphQLCrmObject.allProperties?.reduce<
        Record<string, CrmObjectTypeProperty>
      >((acc, graphQLProperty) => {
        acc[graphQLProperty.name] = {
          source: graphQLProperty.source ?? undefined,
          value: graphQLProperty.value,
        };
        return acc;
      }, {}) ?? {},
    currentUserPermissions: graphQLCrmObject.userPermissions && {
      canView: graphQLCrmObject.userPermissions.currentUserCanView,
      canEdit: graphQLCrmObject.userPermissions.currentUserCanEdit,
      canDelete: graphQLCrmObject.userPermissions.currentUserCanDelete,
      canCommunicate:
        graphQLCrmObject.userPermissions.currentUserCanCommunicate,
    },
    associatedObjects: graphQLCrmObject.associationsBySpec?.reduce(
      (acc, association) => {
        const associationTypeId =
          association.associatedObjects?.edges[0]?.associationDefinition
            .associationTypeId;
        const associationTypeName =
          association.associatedObjects?.edges[0]?.associationDefinition.name;
        if (associationTypeId) {
          acc[associationTypeId] =
            association.associatedObjects?.edges.map((edge) =>
              transformGraphQLCrmObjectToCrmObject(edge.node, objectTypeId)
            ) ?? [];
        }
        if (associationTypeName) {
          acc[associationTypeName] =
            association.associatedObjects?.edges.map((edge) =>
              transformGraphQLCrmObjectToCrmObject(edge.node, objectTypeId)
            ) ?? [];
        }
        return acc;
      },
      {} as NonNullable<CrmObject['associatedObjects']>
    ),
  };
};

export const transformCrmObjectToGraphQLCrmObject = (
  crmObject: CrmObject,
  { existingGqlCrmObject }: { existingGqlCrmObject?: GraphQLCrmObject } = {}
): GraphQLCrmObject => {
  const defaultUserPermissions = existingGqlCrmObject?.userPermissions || {
    currentUserCanView: true,
    currentUserCanEdit: true,
    currentUserCanDelete: true,
    currentUserCanCommunicate: true,
  };
  const defaultAssociationsBySpec =
    existingGqlCrmObject?.associationsBySpec || {};

  return {
    id: Number(crmObject.objectId),
    objectId: Number(crmObject.objectId),
    allProperties: Object.keys(crmObject.properties).map((key) => ({
      id: `${crmObject.objectId}-${key}`,
      name: key,
      value: getPropertyValue(crmObject, key) || null,
      source: crmObject.properties[key]?.source || null,
      __typename: 'PropertyValue',
    })),
    userPermissions: crmObject.currentUserPermissions
      ? {
          currentUserCanView: crmObject.currentUserPermissions.canView,
          currentUserCanEdit: crmObject.currentUserPermissions.canEdit,
          currentUserCanDelete: crmObject.currentUserPermissions.canDelete,
          currentUserCanCommunicate:
            crmObject.currentUserPermissions.canCommunicate,
        }
      : defaultUserPermissions,
    ...(crmObject.associatedObjects
      ? {
          associationsBySpec: Object.keys(crmObject.associatedObjects).map(
            (toObjectTypeDefinitionId) => ({
              associatedObjects: {
                edges:
                  crmObject.associatedObjects?.[toObjectTypeDefinitionId].map(
                    (associatedObject) => ({
                      node: {
                        id: Number(associatedObject.objectId),
                        allProperties: Object.keys(
                          associatedObject.properties
                        ).map((key) => ({
                          id: `${associatedObject.objectId}-${key}`,
                          name: key,
                          value: associatedObject.properties[key].value
                            ? `${associatedObject.properties[key].value}`
                            : null,
                          source:
                            associatedObject.properties[key].source ?? null,
                          __typename: 'PropertyValue',
                        })),
                        objectId: Number(associatedObject.objectId),
                        __typename: 'BasicCrmObject',
                      },
                      associationDefinition: {
                        name: toObjectTypeDefinitionId,
                        associationTypeId: Number(toObjectTypeDefinitionId),
                        associationCategory: 'HUBSPOT_DEFINED',
                      },
                    })
                  ) || [],
              },
            })
          ),
        }
      : defaultAssociationsBySpec),
    __typename: 'BasicCrmObject',
  };
};

/**
 * This method is transforming the GraphQL response to match the REST response since both are slightly different.
 */
export const transformGraphQLResponseIntoCrmSearchResponse = memoize(
  (
    graphQLData: GraphQLCrmSearchCacheType,
    objectTypeId: string
  ): CrmSearchResponse | undefined => {
    if (!graphQLData || !(CRM_SEARCH_GRAPHQL_CACHE_KEY in graphQLData)) {
      return undefined;
    }

    const { offset, results, total, hasMore } =
      graphQLData[CRM_SEARCH_GRAPHQL_CACHE_KEY];

    return {
      total,
      offset,
      results: results.map((graphqlCrmObject) =>
        transformGraphQLCrmObjectToCrmObject(graphqlCrmObject, objectTypeId)
      ),
      hasMore,
    };
  }
);

export const transformCrmSearchResponseToGraphQLResponse = (
  crmSearchResponse: CrmSearchResponse
): GraphQLCrmSearchResponse => {
  return {
    total: crmSearchResponse.total,
    offset: crmSearchResponse.offset,
    results: crmSearchResponse.results.map((crmObject) =>
      transformCrmObjectToGraphQLCrmObject(crmObject)
    ),
    hasMore: crmSearchResponse.hasMore,
  };
};

/**
 * Add a new CRM object to the Apollo cache.
 * This method is used by optimistic updates in listing-lib.
 */
export const addCrmObjectToApolloCache = (
  apolloCache: GraphQLCrmSearchCacheType,
  crmObject: CrmObject
): GraphQLCrmSearchCacheType => {
  return {
    [CRM_SEARCH_GRAPHQL_CACHE_KEY]: {
      ...apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY],
      results: [
        transformCrmObjectToGraphQLCrmObject(crmObject),
        ...apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY].results,
      ],
      total: apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY].total + 1,
    },
  };
};

/**
 * Replace existing CRM objects in cache by the ones provided by @crmObjectOrObjects.
 * This method is used by optimistic updates in listing-lib.
 */
export const updateCrmObjectsInApolloCache = (
  apolloCache: GraphQLCrmSearchCacheType,
  crmObjectOrObjects: CrmObject | CrmObject[]
) => {
  const objectsMap: Record<ObjectId, CrmObject> = Array.isArray(
    crmObjectOrObjects
  )
    ? crmObjectOrObjects.reduce<Record<ObjectId, CrmObject>>((acc, object) => {
        acc[object.objectId] = object;
        return acc;
      }, {})
    : {
        [crmObjectOrObjects.objectId]: crmObjectOrObjects,
      };

  return {
    [CRM_SEARCH_GRAPHQL_CACHE_KEY]: {
      ...apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY],
      results: apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY].results.map(
        (graphqlCrmObject) => {
          if (objectsMap[graphqlCrmObject.objectId]) {
            return transformCrmObjectToGraphQLCrmObject(
              objectsMap[graphqlCrmObject.objectId],
              { existingGqlCrmObject: graphqlCrmObject }
            );
          }

          return graphqlCrmObject;
        }
      ),
    },
  };
};

function updateExistingGraphqlProperty(
  graphqlCrmObject: GraphQLCrmObject,
  propertyValue: UpdatedCrmPropertyValue
): GraphQLCrmObject {
  return {
    ...graphqlCrmObject,
    allProperties:
      graphqlCrmObject.allProperties?.map((graphQLProperty) => {
        if (graphQLProperty.name !== propertyValue.name) {
          return graphQLProperty;
        }

        return {
          ...graphQLProperty,
          value: propertyValue.value === undefined ? null : propertyValue.value,
        };
      }) ?? [],
  };
}

function addNewGraphqlProperty(
  graphqlCrmObject: GraphQLCrmObject,
  propertyValue: UpdatedCrmPropertyValue
): GraphQLCrmObject {
  return {
    ...graphqlCrmObject,
    allProperties: [
      ...(graphqlCrmObject.allProperties ?? []),
      {
        id: `${graphqlCrmObject.id}-${propertyValue.name}`,
        name: propertyValue.name,
        value: propertyValue.value || null,
        source: propertyValue.source ?? null,
        __typename: 'PropertyValue',
      },
    ],
  };
}

/**
 * Update property values from CRM object in cache.
 * This method is used by inline editing in listing-lib.
 */
export const updateGraphQLPropertyValues = (
  apolloCache: GraphQLCrmSearchCacheType,
  selectedIds: Array<ObjectId>,
  propertyValues: UpdatedCrmPropertyValue[]
): GraphQLCrmSearchCacheType => {
  return {
    [CRM_SEARCH_GRAPHQL_CACHE_KEY]: {
      ...apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY],
      results: apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY].results.map(
        (graphqlCrmObject) => {
          if (
            selectedIds.length &&
            !selectedIds.some(
              (id) => String(id) === String(graphqlCrmObject.id)
            )
          ) {
            return graphqlCrmObject;
          }

          let copyGraphQLCrmObject = { ...graphqlCrmObject };

          for (const propertyValue of propertyValues) {
            const isExistingProperty = copyGraphQLCrmObject.allProperties?.find(
              (graphqlProperty) => graphqlProperty.name === propertyValue.name
            );

            if (isExistingProperty) {
              copyGraphQLCrmObject = updateExistingGraphqlProperty(
                copyGraphQLCrmObject,
                propertyValue
              );
            } else {
              copyGraphQLCrmObject = addNewGraphqlProperty(
                copyGraphQLCrmObject,
                propertyValue
              );
            }
          }

          return copyGraphQLCrmObject;
        }
      ),
    },
  };
};

export const getGraphQLVariables = (
  crmSearchVariables: CrmSearchVariables
): GraphQLVariables => ({
  associationSpecs: crmSearchVariables.associationSpecs || [],
  hasAssociationSpecs:
    !!crmSearchVariables.associationSpecs &&
    crmSearchVariables.associationSpecs.length > 0,
  count: crmSearchVariables.fetchParams.count,
  filterGroups: crmSearchVariables.fetchParams.filterGroups,
  objectTypeId: crmSearchVariables.fetchParams.objectTypeId,
  offset: crmSearchVariables.fetchParams.offset,
  query: crmSearchVariables.fetchParams.query,
  sorts: crmSearchVariables.fetchParams.sorts,
  timeZoneId: crmSearchVariables.fetchParams.timeZoneId,
  timeZoneSource: crmSearchVariables.fetchParams.timeZoneSource,
});
